import { Fragment, useCallback, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import { ESendByCallPetitions, ICallback, sendByCall } from '@r1-frontend/api-domru/api-request/v1/user/by-call';
import { backCall } from '@r1-frontend/api-domru/core-requests/api/v1/call/back';
import { dataLayerForCBPhonePush } from '@r1-frontend/entities/Layout/_deps/helpers/eventsForDataLayer';
import RenderMessage
    from '@r1-frontend/entities/Layout/Header/mobile/components/CommonMobileHeader/CallRequestByTheme/RenderMessage';
import { TState } from '@r1-frontend/entities/stores/auth/reducer';
import selectProviderId from '@r1-frontend/entities/stores/city/selectors/selectProviderId';

import { BaseButton, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { ACTIONS } from '@r1-frontend/shared/constants/analytics';
import { POLICY_PAGE_LINK } from '@r1-frontend/shared/constants/routes';

import {
    BaseContainer,
    BreakHack,
    Button,
    ButtonLookLikeHref,
    ButtonsContainer,
    InputContainer,
    NameInput,
    PhoneInput,
    Popup,
    Text,
    Title,
} from './styled';

enum EThemes {
    PROBLEMS_REPORT,
    GET_CONSULTATION,
    BECOME_CLIENT,
    COMPLAIN,
    OTHER
}

type TField = {
    value: string,
    isValid: boolean,
    clearInput: () => void,
}

interface ITheme {
    code: EThemes,
    label: string,
    isHighlight?: boolean,
}

interface IRenderSelect {
    themes: ITheme[],
    HighlightIndex: number,
    onSelectTheme: (theme: ITheme, index: number) => void,
}

const INITIAL_THEME = 2;

const THEMES: ITheme[] = [
    {
        code: EThemes.PROBLEMS_REPORT,
        label: 'Сообщить о неполадках',
    },
    {
        code: EThemes.GET_CONSULTATION,
        label: 'Получить консультацию',
    },
    {
        code: EThemes.BECOME_CLIENT,
        label: 'Стать клиентом',
    },
    {
        code: EThemes.COMPLAIN,
        label: 'Пожаловаться',
    },
    {
        code: EThemes.OTHER,
        label: 'Другое',
    },
];

interface IInputValue {
    readonly isValid: boolean,
    readonly value: string,
    readonly clearInput?: () => void,
}

function useIsValidForm(fio: IInputValue, phone: IInputValue): [boolean] {
    const [isValid, setIsValid] = useState<boolean>(false);
    useEffect(() => {
        setIsValid(fio.isValid && phone.isValid);
    }, [fio, phone]);
    return [isValid];
}

/**
 * @param state
 */
function mapStateToProps(state: TState) {
    return {
        providerId: selectProviderId(state),
    };
}

export default connect(mapStateToProps)(CallRequestByTheme);

function clearFields(fields: IInputValue[]): void {
    fields.forEach(function(field) {
        if (field.clearInput) {
            field.clearInput();
        }
    });
}

interface CallRequestByThemeProps {
    isOpen: boolean,
    onClose: () => void,
    providerId: number,
}

function CallRequestByTheme({ isOpen, onClose, providerId }: CallRequestByThemeProps) {
    const [theme, setTheme] = useState<ITheme>(THEMES[INITIAL_THEME]);
    const [selectBlockVisible, setVisible] = useState<boolean>(true);
    const [isHighlight, setHighlight] = useState<number>(INITIAL_THEME);
    const [fio, setFio] = useState<IInputValue>({ value: '', isValid: false });
    const [phone, setPhone] = useState<IInputValue>({ value: '', isValid: false });
    const [clearTimeoutLink, setClearTimeout] = useState<number | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [message, setMessage] = useState<string | null | undefined>(null);

    const [isValid] = useIsValidForm(fio, phone);

    const isAuth = useSelector((state: TState) => state.auth.isAuth);
    const clientPersonal = useSelector((state: TState) => state.auth.clientPersonal);

    // @TODO заплатка - потому что clearFields не очищает phone
    const clearFieldsPatch = () => {
        setFio({ value: '', isValid: false });
        setPhone({ value: '', isValid: false });
    };

    const handleClose = useCallback(() => {
        setVisible(true);
        setHighlight(INITIAL_THEME);
        setIsError(false);
        setMessage(null);
        clearFields([fio, phone]);
        clearFieldsPatch();
        if (clearTimeoutLink) {
            clearTimeout(clearTimeoutLink);
        }
        onClose();
    }, [fio, phone, clearTimeoutLink]);

    const handleSendRequest = useCallback(async() => {
        if (theme) {
            setIsError(false);
            setMessage(null);
            if (clearTimeoutLink) {
                clearTimeout(clearTimeoutLink);
            }
            setIsLoading(true);
            setMessage(null);

            let resp;
            if (theme.code === EThemes.BECOME_CLIENT) {
                resp = await doRequest.apiRequest<ICallback>(
                    sendByCall(providerId, {
                        fio: fio.value,
                        phone: phone.value,
                        petition: ESendByCallPetitions.RequestNew,
                    }),
                );

                dataLayerForCBPhonePush({
                    action: resp.isSuccess ? ACTIONS.finished : ACTIONS.error,
                    requestId: null,
                    option: resp.isSuccess ? `${theme.label}` : `${theme.label} | ${resp.error.message}`,
                    isPopup: true,
                    uid: isAuth ? clientPersonal?.agreement : null,
                });
            } else {
                resp = await doRequest.coreRequests<unknown>(
                    backCall({
                        phoneNumber: phone.value,
                    }),
                );
                dataLayerForCBPhonePush({
                    action: resp.isSuccess ? ACTIONS.orderService : ACTIONS.errorService,
                    requestId: null,
                    option: resp.isSuccess ? `${theme.label}` : `${theme.label} | ${resp.error.message}`,
                    isPopup: true,
                    uid: isAuth ? clientPersonal?.agreement : null,
                });
            }

            clearFields([fio, phone]);
            clearFieldsPatch();

            setIsLoading(false);
            if (resp.isSuccess) {
                setIsError(false);
                setMessage('Заявка успешно создана');
            } else {
                setIsError(true);
                setMessage(resp.error.message ?? undefined);
            }
            setClearTimeout(setTimeout(() => {
                setIsError(false);
                setMessage(null);
            }, 30000) as unknown as number);
        }
    }, [fio, phone, theme, clearTimeoutLink, providerId]);

    const onSelectHandler = (theme: ITheme, index: number) => {
        setVisible(false);
        setTheme(theme);
        setHighlight(index);
    };

    const onChangeHandler = () => {
        setVisible(true);
        clearFieldsPatch();
    };

    return <Popup isOpen={isOpen} onClose={handleClose}>
        {
            selectBlockVisible
                ? <RenderSelectTheme
                    themes={THEMES}
                    HighlightIndex={isHighlight}
                    onSelectTheme={onSelectHandler}/>
                : <RenderForm
                    fio={fio}
                    onChangeTheme={onChangeHandler}
                    onEditFio={setFio}
                    onEditPhone={setPhone}
                    message={message}
                    onSendRequest={handleSendRequest}
                    phone={phone}
                    theme={theme}
                    isValid={isValid}
                    isLoading={isLoading}
                    isError={isError}/>
        }
    </Popup>;
}

function RenderSelectTheme({ themes, onSelectTheme, HighlightIndex }: IRenderSelect) {
    return <Fragment>
        <Title>Выберите тему обращения</Title>
        <ButtonsContainer data-test='popup-theme'>
            {
                themes.map(function(theme, index) {
                    return (<BaseButton
                        key={theme.code}
                        text={theme.label}
                        onClick={() => {
                            onSelectTheme(theme, index);
                        }}
                        type={HighlightIndex === index ? btnTypes.PRIMARY : btnTypes.SECONDARY}
                        data-test='popup-theme-choose'
                    />);
                })
            }
        </ButtonsContainer>
    </Fragment>;
}

interface IRenderFormProps {
    readonly theme: ITheme,
    readonly fio: IInputValue,
    readonly phone: IInputValue,
    readonly onEditFio: (fio: IInputValue) => void,
    readonly onEditPhone: (phone: IInputValue) => void,
    readonly onChangeTheme: () => void,
    readonly onSendRequest: () => void,
    readonly isValid: boolean,
    readonly isLoading: boolean,
    readonly isError: boolean,
    readonly message: string | null | undefined,
}

function RenderForm({
    theme,
    fio,
    phone,
    onEditFio,
    onEditPhone,
    onChangeTheme,
    onSendRequest,
    isValid,
    isLoading,
    isError,
    message,
}: IRenderFormProps) {
    const [beenStarted, setDataLayer] = useState(false);

    const isAuth = useSelector((state: TState) => state.auth.isAuth);
    const clientPersonal = useSelector((state: TState) => state.auth.clientPersonal);

    const onChangeHandler = useCallback((stateInput: IInputValue, _setState: (value: IInputValue) => void) => {
        const stateResult = typeof stateInput === 'object' ? stateInput.value : stateInput;
        // @TODO stateInput.value убрать из условия после фикса nameDecorator (useEffect onchange) UI react
        if (!beenStarted && stateResult) {
            dataLayerForCBPhonePush({
                action: theme.code === EThemes.BECOME_CLIENT ? ACTIONS.started : ACTIONS.startedService,
                requestId: null,
                option: `${theme.label}`,
                isPopup: true,
                uid: isAuth ? clientPersonal?.agreement : null,
            });
            setDataLayer(true);
        }
        _setState(stateInput);
    }, [beenStarted, theme.label]);

    const buttonText = 'Отправить заявку';

    return <Fragment>
        <Title data-test='popup-request-theme'>Тема обращения: {theme.label}</Title>
        <Text>
            Оставьте номер телефона, и менеджер вам перезвонит.&nbsp;
            <BreakHack>Или<ButtonLookLikeHref onClick={onChangeTheme}>измените тему
                обращения</ButtonLookLikeHref>.</BreakHack>
        </Text>
        <InputContainer>
            <NameInput initialValue={fio.value}
                onChange={(stateInput: TField) => onChangeHandler(stateInput, onEditFio)}
                placeholder={'Ваше имя'}
                errorMessage={'Ожидаются только буквы и дефисы'}
                name='request-input-name'
                className={'ym-disable-keys'}
            />
            <PhoneInput initialValue={phone.value}
                onChange={(stateInput: TField) => onChangeHandler(stateInput, onEditPhone)}
                placeholder={'Номер телефона'}
                errorMessage={'Введите телефон в формате +7 (999) 999-99-99'}
                name='request-input-phone'
                className={'ym-disable-keys'}
            />
        </InputContainer>

        <RenderMessage isError={isError} message={message}/>

        <BaseContainer>
            <PolicyNote
                data-test='agreement'
                buttonText={buttonText}
                agreeUrl={POLICY_PAGE_LINK}
            />
            <Button
                type={btnTypes.ACCENT}
                onClick={onSendRequest}
                disabled={!isValid || isLoading}
                data-test='request-button-form'
            >
                {buttonText}
            </Button>
        </BaseContainer>
    </Fragment>;
}
